/**
 * Переменные-ссылки на материалы в промптах — доступ к общему определению (spec.json).
 *
 * МАСТЕР — shared/prompt-vars; этот файл и spec.json копирует в frontend/src/prompt-vars и
 * admin/src/prompt-vars скрипт scripts/sync_prompt_vars.sh (CI и тесты ловят расхождение).
 * Править только мастер. Модуль не импортирует ничего из приложений — он одинаков в обоих.
 *
 * Три слоя одного и того же:
 *   токен конструктора  {video}            — шаблоны карточек в админке;
 *   каноническая фраза  the reference video — текст промпта на сайте и в мастерской;
 *   токен провайдера    @Video1 / @video_1  — что получает модель (переводит сервер),
 *                                              и что показывает подпись пилюли.
 */
import spec from './spec.json'

export type PromptVarKey = 'photo' | 'first_frame' | 'last_frame' | 'character' | 'video' | 'audio' | 'variant' | 'value'
export type PromptKind = 'image' | 'video'
export type PromptRefStyle = 'seedance' | 'kling' | 'wan'
export type PromptVarPlace = 'site' | 'admin' | 'flows'
export type PromptChannel = 'image' | 'video' | 'audio'

export interface PromptVarDef {
	key: PromptVarKey
	channel?: PromptChannel | null
	numbered?: boolean
	max?: number
	/** Токен конструктора карточек: '{photo{n}}', '{video}'. */
	token: string
	/** Каноническая фраза в тексте промпта (одна или по типу модели). */
	phrase?: string | Record<PromptKind, string>
	legacy_phrases?: string[]
	label?: string | Record<PromptKind, string>
	sub?: string
	empty?: string
	admin_label?: string
	admin_sub?: string
	/** Семантический цвет пилюли ('photo{n}', 'frame1', …) — класс подбирает приложение. */
	color: string
	places: PromptVarPlace[]
}

interface PromptVarsSpec {
	version: number
	styles: Record<PromptRefStyle, Partial<Record<PromptChannel, string>>>
	style_by_model: { match?: string; provider?: string; style: PromptRefStyle }[]
	ordinals: string[]
	vars: PromptVarDef[]
}

export const PROMPT_VARS_SPEC = spec as unknown as PromptVarsSpec

const fill = (tpl: string, n?: number) => tpl.replace(/\{n\}/g, String(n ?? 1))
const byKind = (v: string | Record<PromptKind, string> | undefined, kind: PromptKind): string | undefined =>
	typeof v === 'string' ? v : v ? v[kind] : undefined

export function promptVar(key: PromptVarKey): PromptVarDef {
	const def = PROMPT_VARS_SPEC.vars.find((v) => v.key === key)
	if (!def) throw new Error(`prompt var ${key} is not in spec.json`)
	return def
}

/** Все переменные для места (site / admin / flows) в порядке спеки. */
export function promptVarsFor(place: PromptVarPlace): PromptVarDef[] {
	return PROMPT_VARS_SPEC.vars.filter((v) => v.places.includes(place))
}

/** Токен конструктора: '{photo2}', '{video}'. */
export function varToken(key: PromptVarKey, n?: number): string {
	return fill(promptVar(key).token, n)
}

/** Каноническая фраза для текста промпта: 'Image 2', 'the first frame image'. */
export function varPhrase(key: PromptVarKey, kind: PromptKind = 'image', n?: number): string {
	const p = byKind(promptVar(key).phrase, kind)
	if (p == null) throw new Error(`prompt var ${key} has no phrase`)
	return fill(p, n)
}

/** Сколько нумерованных слотов у переменной (фото: 4). */
export function varMax(key: PromptVarKey): number {
	return promptVar(key).max ?? 1
}

/** Старые фразы фото по рангу: [['the first image', …], ['the first reference image', …]]. */
export function legacyPhotoPhrases(): string[][] {
	const def = promptVar('photo')
	return (def.legacy_phrases ?? []).map((tpl) => PROMPT_VARS_SPEC.ordinals.map((o) => tpl.replace(/\{ordinal\}/g, o)))
}

/** Подпись пилюли на сайте и в мастерской. */
export function varLabel(key: PromptVarKey, kind: PromptKind = 'image', n?: number): string {
	const def = promptVar(key)
	return fill(byKind(def.label, kind) ?? def.admin_label ?? key, n)
}

/** Подпись чипа в конструкторе карточек. */
export function adminLabel(key: PromptVarKey, n?: number): string {
	const def = promptVar(key)
	return fill(def.admin_label ?? byKind(def.label, 'image') ?? key, n)
}

/** Токен провайдера для канала: ('image', 2, 'seedance') → '@Image2'; null — у стиля нет такого канала. */
export function providerToken(channel: PromptChannel | null | undefined, n: number, style: PromptRefStyle | null | undefined): string | null {
	if (!channel || !style) return null
	const tpl = PROMPT_VARS_SPEC.styles[style]?.[channel]
	return tpl ? fill(tpl, n) : null
}

/** Синтаксис ссылок выбранной модели: флаг из возможностей модели, иначе правила style_by_model. */
export function promptRefStyleOf(
	model: { id?: string; backendModel?: string; provider?: string; promptRefStyle?: string } | null | undefined,
): PromptRefStyle | null {
	if (!model) return null
	if (model.promptRefStyle === 'seedance' || model.promptRefStyle === 'kling' || model.promptRefStyle === 'wan') return model.promptRefStyle
	const id = `${model.backendModel || ''} ${model.id || ''}`.toLowerCase()
	for (const rule of PROMPT_VARS_SPEC.style_by_model) {
		if (rule.match && id.includes(rule.match)) return rule.style
		if (rule.provider && model.provider === rule.provider) return rule.style
	}
	return null
}

/** Цвет пилюли фото по номеру: восемь семей по кругу (полей у карточки до 30 — photo.max, 18.10); классы
 *  gm__ppill--p1…p8 определяют сайт (_modal.scss) и админка (editor.css) одинаковыми семьями. */
export const PILL_RANK_FAMILIES = 8
export const pillRankCls = (n: number): string => `gm__ppill--p${((Math.max(1, n) - 1) % PILL_RANK_FAMILIES) + 1}`

const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/** Все токены конструктора одним списком: ['{photo1}', …, '{photo4}', '{first_frame}', …]. */
export function allTokens(place: PromptVarPlace = 'admin'): string[] {
	const out: string[] = []
	for (const v of promptVarsFor(place)) {
		if (v.numbered) for (let n = 1; n <= (v.max ?? 1); n++) out.push(fill(v.token, n))
		else out.push(v.token)
	}
	return out
}

/** Регэксп-разделитель по токенам конструктора (с захватом — для split). */
export function tokenSplitRegex(place: PromptVarPlace = 'admin'): RegExp {
	return new RegExp(`(${allTokens(place).map(esc).join('|')})`)
}

/** Разбор токена: '{photo3}' → { key: 'photo', n: 3 }; неизвестный — null. */
export function parseToken(token: string): { key: PromptVarKey; n?: number } | null {
	for (const v of PROMPT_VARS_SPEC.vars) {
		if (v.numbered) {
			const re = new RegExp(`^${esc(v.token).replace(/\\\{n\\\}/g, '(\\d+)')}$`)
			const m = token.match(re)
			if (m) return { key: v.key, n: Number(m[1]) }
		} else if (token === v.token) return { key: v.key }
	}
	return null
}
